import {
  Box3,
  Mesh,
  Object3D,
  Vector3
} from 'three';
import { DragControls } from 'three/examples/jsm/controls/DragControls.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { LoadType, PartSubType } from '../../models/enums';
import { LoadsGroupItem } from '../../models/loadcase.model';
import { ArrowHelper } from '../addons/load-helpers/arrowhelper';
import {
  LOAD_INDICATOR_DEFAULT_LENGTH,
  LOAD_SPHERE_RADIUS
} from '../engine.constants';
import {
  OVERLAY_COLORS,
  OVERLAY_RENDER_ORDER,
  OVERLAY_THIN_LINE_WIDTH_CSS_PX,
  createOverlayLine,
  createOverlayLineMaterial,
  createOverlayMeshMaterial,
  createOverlaySphereGeometry
} from '../addons/visualization/overlay-style';
import {
  calculateCenter,
  calculateCenterOfMass,
  calculateVolume,
  getLoadIndicatorScale
} from '../helpers';
import { EngineContext } from './engine-context';
import { resolveDisplayVectors } from '../../utils/local-frame-display.util';
import { colorForSystemId } from '../../utils/coordinate-systems.util';
import { deriveAxesFromDefinition } from '../../utils/local-frame.util';

/**
 * Manages load case visualization: reaction forces, load case highlighting,
 * group edit stacks with force/moment arrows.
 * Extracted from Engine to reduce class size.
 */
export class LoadCaseVisualizationManager {
  constructor(private ctx: EngineContext) {}

  /**
   * Re-applies the current indicator size to everything already on screen.
   *
   * Indicators are built at their size rather than scaled afterwards, so a
   * change to the annotation-size setting only reaches existing markers
   * through here. Unlike the highlight rebuild this needs no load case to be
   * active -- the setting can be changed from anywhere.
   */
  refreshIndicatorSizes(): void {
    this.resizeLoadIndicators();

    // The arrows shown while a load or group is being edited live on the
    // group's edit stack, not in selector.objects, so they are missed by the
    // per-part pass above.
    const scale = getLoadIndicatorScale(this.ctx.objectInfo.size);
    const groups = this.ctx.session.project?.loadcases?.flatMap(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (loadcase: any) => loadcase?.groups ?? []
    ) ?? [];
    for (const group of groups) {
      group?.groupEditStack?.force?.setLength(scale);
      group?.groupEditStack?.moment?.setLength(scale);
    }
  }

  private resizeLoadIndicators(): void {
    const scale = getLoadIndicatorScale(this.ctx.objectInfo.size);
    const sphereScale = scale / LOAD_INDICATOR_DEFAULT_LENGTH;
    // The engine's own list, which the selector shares: it is already filled
    // while the engine is still loading, before session.engine is set.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.ctx.objects.forEach((obj: any) => {
      const partArrow = obj.group.userData['part_arrow'] as ArrowHelper | undefined;
      const partMoment = obj.group.userData['part_moment'] as ArrowHelper | undefined;
      const partSphere = obj.group.userData['part_sphere'] as Mesh | undefined;
      const referenceSphere = obj.group.userData['part_reference_sphere'] as Mesh | undefined;
      partArrow?.setLength(scale);
      partMoment?.setLength(scale);
      partSphere?.scale.setScalar(sphereScale);
      referenceSphere?.scale.setScalar(sphereScale);
    });
  }

  toggleReactionForces(value?: boolean): void {
    // Hide first (both label and arrow)
    this.hideAllReactionForceVisuals();

    if (value) {
      (this.ctx as unknown as { reactionForces: boolean }).reactionForces = value;
    } else {
      (this.ctx as unknown as { reactionForces: boolean }).reactionForces =
        !(this.ctx as unknown as { reactionForces: boolean }).reactionForces;
    }

    this.refreshReactionForceVisuals();
  }

  /**
   * Re-render reaction-force labels and arrows using the *current* unit
   * system. No-op when the toggle is OFF. Call this from an Angular `effect`
   * watching `unitConversionService.unitSystem` so the labels live-update
   * when the user flips metric ↔ imperial in Project Settings.
   */
  refreshReactionForceVisuals(): void {
    // Bail when the toggle is OFF — leaves visuals hidden as-is.
    const showRequested = (this.ctx as unknown as { reactionForces: boolean }).reactionForces;
    if (!showRequested) return;

    // Hide first so a re-render starts from a clean state (avoids stale
    // arrows/labels for parts no longer in the current loadcase set).
    this.hideAllReactionForceVisuals();

    try {
      if (this.ctx.session.reactionForcesAvailable && this.ctx.session.reactionForces) {
        // reaction_force.json lists load cases in the run's order (Harmonic
        // Excitation before Static), which the result load cases follow.
        const currentId = this.ctx.session.engine?.currentLoadCase;
        const resultIndex = ((this.ctx.assemblyOptimizedInfo ?? []) as { id?: string }[]).findIndex(
          (info) => info?.id === currentId
        );
        const loadCaseIndex =
          resultIndex >= 0
            ? resultIndex
            : this.ctx.session.project.loadcases.findIndex(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (lc: any) => lc.id === currentId
              );
        const reactionForce = (this.ctx.session.reactionForces as unknown[])[loadCaseIndex] as
          | { id: string; x: number; y: number; z: number }[]
          | undefined;

        // Scale arrow length proportional to magnitude. baseLen is the
        // applied-load arrow length; reaction-force arrows are intentionally
        // smaller (max ~70% of an applied-load arrow) so they don't dominate
        // the scene when many constraints react. The min keeps tiny reactions
        // pickable.
        const baseLen = getLoadIndicatorScale(this.ctx.objectInfo.size);
        const minScale = 0.2;
        const maxScale = 0.7;
        // Find max magnitude across all reactions in this loadcase to normalise.
        const maxMag = (reactionForce ?? []).reduce((acc, p) => {
          const m = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
          return m > acc ? m : acc;
        }, 0);

        // The reactions are in the UCS frame, like the holder the arrows live
        // in, or in the model's own frame after an FEA run.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.ctx.session.engine?.objects.forEach((obj: any) => {
          const control = obj.group.userData.part_reaction_force_annotation;
          const arrow = obj.group.userData.part_reaction_force_arrow as ArrowHelper | undefined;
          const match = reactionForce?.find((p) => p.id === obj.data.id);
          if (!match) return;
          // At the part as it is shown, in holder coordinates.
          const centre = this.ctx.toUcsFrame(calculateCenter(obj.group));

          const magnitude = Math.sqrt(match.x * match.x + match.y * match.y + match.z * match.z);
          const force = new Vector3(match.x, match.y, match.z);
          // The label gives the components along the model's own axes, the ones shown.
          const shown = this.ctx.resultsInModelFrame ? force.clone() : this.ctx.ucsDirectionToModel(force);

          // Convert to user's unit system (Imperial → lbf)
          const sys = this.ctx.unitConversionService.unitSystem();
          const xConv = this.ctx.unitConversionService.convert(shown.x, 'force', sys);
          const yConv = this.ctx.unitConversionService.convert(shown.y, 'force', sys);
          const zConv = this.ctx.unitConversionService.convert(shown.z, 'force', sys);
          const magConv = this.ctx.unitConversionService.convert(magnitude, 'force', sys);
          const unit = this.ctx.unitConversionService.getUnitLabel('force', sys);

          control.setAnnotationText(
            this.formatForceVal(xConv),
            this.formatForceVal(yConv),
            this.formatForceVal(zConv),
            this.formatForceVal(magConv),
            unit,
          );
          control.annotation.position.copy(centre);
          control.annotation.visible = true;

          // Update the 3D arrow: direction (normalized) + length (scaled by
          // magnitude relative to the loadcase max).
          if (arrow && magnitude > 0) {
            const direction = (this.ctx.resultsInModelFrame ? this.ctx.modelDirectionToUcs(force) : force.clone()).normalize();
            arrow.position.copy(centre);
            arrow.setDirection(direction);
            const ratio = maxMag > 0 ? magnitude / maxMag : 1;
            const scaledLen = baseLen * (minScale + (maxScale - minScale) * ratio);
            arrow.setLength(scaledLen);
            arrow.visible = true;
          } else if (arrow) {
            // Zero-magnitude reaction (constraint with no load reacting through
            // it) — show the label but no arrow (would be degenerate).
            arrow.visible = false;
          }
        });
      }
    } catch (_) {}
  }

  hideReactionForces(): void {
    this.hideAllReactionForceVisuals();
    (this.ctx as unknown as { reactionForces: boolean }).reactionForces = false;
  }

  private hideAllReactionForceVisuals(): void {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.ctx.session.engine?.objects.forEach((obj: any) => {
        const control = obj.group.userData.part_reaction_force_annotation;
        if (control?.annotation) control.annotation.visible = false;
        const arrow = obj.group.userData.part_reaction_force_arrow as ArrowHelper | undefined;
        if (arrow) arrow.visible = false;
      });
    } catch (_) {}
  }

  private formatForceVal(value: number): string {
    if (!Number.isFinite(value)) return '0';
    // Compact display: integers when |v| ≥ 1, fixed-2 otherwise. Keeps the
    // label tight while preserving precision for sub-newton values.
    if (Math.abs(value) >= 100) return value.toFixed(0);
    if (Math.abs(value) >= 1) return value.toFixed(1);
    return value.toFixed(3);
  }

  toggleLoadCaseHighlight(value?: boolean): void {
    if (this.ctx.session.engine) {
      if (value) {
        this.ctx.session.engine.highlightLoadCase = value;
      } else {
        this.ctx.session.engine.highlightLoadCase = !this.ctx.session.engine?.highlightLoadCase;
      }

      if (this.ctx.session.engine?.highlightLoadCase) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const loadcase = this.ctx.session.project.loadcases.find((o: any) => o.id === this.ctx.session.engine?.currentLoadCase);
        if (loadcase) {
          // Drop markers for loads/BCs that no longer exist before rebuilding from the current loadcase.
          this.ctx.session.engine?.clearDomainFrameTriads();
          this.resizeLoadIndicators();
          for (const bc of loadcase.boundaryConditions) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const obj = this.ctx.session.engine?.selector.objects.find((o: any) => bc.partId === o.data.id);
            this.ctx.session.engine.selector.showBoundaryConditionAnnotation(
              obj.group,
              bc,
              this.ctx.session.project?.coordinateSystems ?? []
            );
            this.ctx.session.engine.selector.setPartColorBySubType(obj, PartSubType.BOUNDARY_CONDITION);
          }
          for (const load of loadcase.loads) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const obj = this.ctx.session.engine?.selector.objects.find((o: any) => load.partId === o.data.id);
            this.ctx.session.engine.selector.showLoadAnnotation(obj.group);
            this.ctx.session.engine.selector.showForceAndMomentWithManipulator(obj.group);
            this.ctx.session.engine.selector.setForceAndMomentWithManipulator(obj.group, load);
            this.ctx.session.engine.selector.dragForceAndMomentWithManipulator(obj.group, false);
            this.ctx.session.engine.selector.setPartColorBySubType(obj, PartSubType.LOAD);
          }
          for (const group of loadcase.groups) {
            for (const part of group.parts) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const obj = this.ctx.session.engine?.selector.objects.find((o: any) => part.id === o.data.id);
              this.ctx.session.engine?.selector.setPartColorBySubType(obj, PartSubType.LOAD);
              this.ctx.session.engine?.selector.hideForceAndMomentWithManipulator(obj.group);
            }
            this.createGroupEditStack(group);
            this.positionGroupEditStack(group);
            this.setForceAndMomentWithManipulator(true, group);
          }
        }
      } else {
        this.ctx.session.engine.selector.disableFaceSelection();
        this.ctx.session.engine.selector.restorePartsColorsToDesignAndNonDesign();
        for (const obj of this.ctx.session.engine.selector.objects) {
          this.ctx.session.engine.selector.hideBoundaryConditionAnnotation(obj.group);
          this.ctx.session.engine.selector.hideLoadAnnotation(obj.group);
          this.ctx.session.engine.selector.hideForceAndMomentWithManipulator(obj.group);
        }
        for (const loadcase of this.ctx.session.project.loadcases) {
          if (loadcase.groups?.length) {
            for (const group of loadcase.groups) {
              this.disposeGroupEditStack(group);
            }
          }
        }
        // highlightLoadCase's setter clears domain markers when set to false.
      }
    }
  }

  positionGroupEditStack(group: LoadsGroupItem, force?: boolean): void {
    if (
      (Number.isNaN(group.remotePoint[0]) &&
        Number.isNaN(group.remotePoint[1]) &&
        Number.isNaN(group.remotePoint[2])) ||
      force
    ) {
      const objs = [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const _objs = this.ctx.session.engine?.selector.objects.filter((o: any) =>
        group.parts.find((p) => p.id === o.data.id)
      );
      for (const obj of _objs) {
        const volume = calculateVolume(obj.data.faces);
        const center = calculateCenter(obj.group);
        objs.push({ center, volume });
      }

      // Stored in the model's own coordinates, like every remote point.
      const com = calculateCenterOfMass(objs);

      group.remotePoint[0] = com.x;
      group.remotePoint[1] = com.y;
      group.remotePoint[2] = com.z;
    }

    const vec = new Vector3(group.remotePoint[0], group.remotePoint[1], group.remotePoint[2]);
    group.groupEditStack?.controlMass.sphere!.position.copy(vec);
    group.groupEditStack?.force!.position.copy(vec);
    group.groupEditStack?.moment!.position.copy(vec);

    this.generateGroupEditStack(group);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createGroupEditStack(group: LoadsGroupItem): any {
    const arrowLength = getLoadIndicatorScale(this.ctx.objectInfo.size);
    const sphereRadius = arrowLength * (LOAD_SPHERE_RADIUS / LOAD_INDICATOR_DEFAULT_LENGTH);

    const CreateArrow = (name: string, type: 'line' | 'circle', dir: Vector3, color: number, center: Vector3): ArrowHelper => {
      const start = new Vector3(0, 0, arrowLength);
      const end = new Vector3(0, 0, 0);
      const direction = start.clone().sub(end);
      const length = direction.length();
      const obj = new ArrowHelper(type, direction.normalize(), end, length, color);
      obj.setDirection(dir);
      obj.name = name;
      obj.position.copy(center);
      obj.layers.set(0);
      return obj;
    };

    const CreateDragControl = (
      name: string,
      color: number,
      center: Vector3,
      _enabled: boolean = true
    ): { drag: DragControls; sphere: Mesh } => {
      const sphere = new Mesh(createOverlaySphereGeometry(sphereRadius), createOverlayMeshMaterial(color));
      sphere.renderOrder = OVERLAY_RENDER_ORDER;
      sphere.name = name;
      sphere.position.copy(center);
      sphere.layers.set(0);

      const controls = new DragControls([sphere], this.ctx.camera, this.ctx.canvas);
      controls.enabled = false;

      sphere.userData['dragControl'] = controls;

      return { drag: controls, sphere };
    };

    const arrowForce = CreateArrow('part_arrow', 'line', new Vector3(0, 0, 0), OVERLAY_COLORS.force, new Vector3());
    const arrowMoment = CreateArrow('part_moment', 'circle', new Vector3(0, 0, 0), OVERLAY_COLORS.moment, new Vector3());
    CreateDragControl('groupEditControl', OVERLAY_COLORS.groupEditControl, new Vector3(), false);
    const dragControlMass = CreateDragControl('groupEditControlMass', OVERLAY_COLORS.massSphere, new Vector3(), true);

    const CreateDragControlMassAnnotation = (name: string, parent: Object3D, center: Vector3): CSS2DObject => {
      const div = document.createElement('div');
      div.className = 'annotation-box-text';
      const content = document.createElement('div');
      content.className = 'annotation-content';
      content.textContent = 'Mass';
      div.appendChild(content);

      const annotation = new CSS2DObject(div);
      annotation.name = name;
      annotation.position.copy(center);
      annotation.visible = false;
      parent.add(annotation);

      return annotation;
    };
    const dragControlMassBox = new Box3().setFromObject(dragControlMass.sphere);
    const dragControlMassCenter = dragControlMassBox.getCenter(new Vector3());
    dragControlMassCenter.z -= dragControlMassBox.getSize(new Vector3()).z * 1.5;
    const dragControlMassAnnotation = CreateDragControlMassAnnotation(
      'dragControlMassAnnotation',
      dragControlMass.sphere,
      dragControlMassCenter
    );

    this.ctx.partHelperGroup.add(arrowForce, arrowMoment, dragControlMass.sphere);

    group.groupEditStack = {
      force: arrowForce,
      moment: arrowMoment,
      controlMass: {
        sphere: dragControlMass.sphere,
        drag: dragControlMass.drag,
        annotation: dragControlMassAnnotation
      },
      resources: {
        lines: [],
        materials: []
      }
    };

    return group.groupEditStack;
  }

  disposeGroupEditStack(group: LoadsGroupItem): void {
    if (group.groupEditStack?.resources.lines?.length) {
      for (const line of group.groupEditStack.resources.lines) {
        line.removeFromParent();
        line.geometry.dispose();
      }
    }

    if (group.groupEditStack?.resources.materials?.length) {
      for (const mat of group.groupEditStack.resources.materials) {
        mat.dispose();
      }
    }

    // These arrows are the bespoke ArrowHelper, which builds its own geometry
    // and material per instance rather than sharing three's statics -- so
    // removing them from the scene leaves four GPU objects behind each. The
    // stack is rebuilt on every load-case highlight toggle.
    if (group.groupEditStack?.force) {
      group.groupEditStack.force.removeFromParent();
      group.groupEditStack.force.dispose?.();
    }
    if (group.groupEditStack?.moment) {
      group.groupEditStack.moment.removeFromParent();
      group.groupEditStack.moment.dispose?.();
    }
    if (group.groupEditStack?.controlMass?.sphere) {
      const sphere = group.groupEditStack.controlMass.sphere as Mesh;
      sphere.removeFromParent();
      sphere.geometry?.dispose();
      const sphereMaterial = sphere.material;
      if (Array.isArray(sphereMaterial)) sphereMaterial.forEach((m) => m.dispose());
      else sphereMaterial?.dispose();
    }
    // setForceAndMomentWithManipulator draws a group's triad outside the
    // load-case-highlight rebuild (ordinary editing), so disposing the edit
    // stack is that triad's other exit and clears the same key.
    this.ctx.session.engine?.clearFrameTriad(`group-${group.id}`);

    if (group.groupEditStack?.controlMass?.drag) group.groupEditStack.controlMass.drag.dispose();
    if (group.groupEditStack?.controlMass?.annotation) group.groupEditStack.controlMass.annotation.visible = false;
    if (group.groupEditStack?.controlMass?.annotation) group.groupEditStack.controlMass.annotation.removeFromParent();

    if (group.groupEditStack) {
      group.groupEditStack.controlMass.annotation = undefined;
      group.groupEditStack.controlMass.drag = undefined;
      group.groupEditStack.controlMass.sphere = undefined;
      group.groupEditStack.force = undefined;
      group.groupEditStack.moment = undefined;
      group.groupEditStack.resources.lines = undefined;
      group.groupEditStack.resources.materials = undefined;
    }
  }

  generateGroupEditStack(group: LoadsGroupItem): void {
    const CreateLinkLine = (to: Vector3, _from?: Vector3, color: number = OVERLAY_COLORS.link): void => {
      const start = group.groupEditStack!.controlMass.sphere!.position;
      const matLine = createOverlayLineMaterial(color, { widthCssPx: OVERLAY_THIN_LINE_WIDTH_CSS_PX, dashed: true });
      const line = createOverlayLine([start.x, start.y, start.z, to.x, to.y, to.z], matLine);

      group.groupEditStack!.resources.lines!.push(line);
      group.groupEditStack!.resources.materials!.push(matLine);

      this.ctx.partHelperGroup.add(line);
    };

    for (const op of group.parts) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const obj = this.ctx.session.engine?.selector.objects.find((o: any) => op.id === o.data.id);
      CreateLinkLine(calculateCenter(obj.group));
    }
  }

  setForceAndMomentWithManipulator(updateControl: boolean = false, group: LoadsGroupItem): void {
    const remotePoint = new Vector3(group.remotePoint[0], group.remotePoint[1], group.remotePoint[2]);
    // Drawn in global - see resolveDisplayVectors.
    const { force: forceComponent, moment: momentComponent } = resolveDisplayVectors(
      group,
      this.ctx.session.project?.coordinateSystems ?? []
    );

    if (updateControl || group.type === LoadType.FORCE) group.groupEditStack?.moment!.position.copy(remotePoint);
    if (updateControl || group.type === LoadType.FORCE) group.groupEditStack?.force!.position.copy(remotePoint);

    group.groupEditStack?.controlMass.sphere!.position.copy(remotePoint);

    group.groupEditStack?.moment!.setDirection(momentComponent);
    group.groupEditStack?.force!.setDirection(forceComponent);

    if (forceComponent.x === 0 && forceComponent.y === 0 && forceComponent.z === 0) {
      group.groupEditStack?.force!.children.forEach((o) => o.layers.set(1));
    } else {
      group.groupEditStack?.force!.children.forEach((o) => o.layers.set(0));
    }

    if (momentComponent.x === 0 && momentComponent.y === 0 && momentComponent.z === 0) {
      group.groupEditStack?.moment!.children.forEach((o) => o.layers.set(1));
    } else {
      group.groupEditStack?.moment!.children.forEach((o) => o.layers.set(0));
    }

    const systems = this.ctx.session.project?.coordinateSystems ?? [];
    const frameColor = colorForSystemId(systems, group.coordinateSystemId ?? null);
    // colorForSystemId returns non-null iff the id resolves, so this find is
    // expected to hit whenever frameColor is set - branched rather than
    // asserted so that coupling cannot silently regress into a crash.
    const system = frameColor ? systems.find((s) => s.id === group.coordinateSystemId) : undefined;
    const axes = system ? deriveAxesFromDefinition(system.definition) : null;
    const triadKey = `group-${group.id}`;
    if (frameColor && axes) {
      group.groupEditStack?.force!.setColor(frameColor);
      this.ctx.session.engine?.drawFrameTriad(triadKey, remotePoint, axes, frameColor);
    } else {
      // Global frame, or a resolved-but-degenerate frame: restore the
      // default force-arrow colour in case a previously assigned frame
      // tinted it, and don't draw a triad for a direction the arrow isn't
      // actually following.
      group.groupEditStack?.force!.setColor(OVERLAY_COLORS.force);
      this.ctx.session.engine?.clearFrameTriad(triadKey);
    }
  }
}
